import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { classNames } from "./utils";

function addDays(base, days) {
  const next = new Date(base);
  next.setDate(next.getDate() + days);
  return next;
}

function nextMorning(base, addDays = 0) {
  const next = new Date(base);
  next.setDate(next.getDate() + addDays);
  next.setHours(9, 0, 0, 0);
  return next;
}

const PRESETS = [
  { key: "tomorrow", label: "Tomorrow 9am", build: () => nextMorning(new Date(), 1) },
  { key: "in_3d", label: "In 3 days", build: () => nextMorning(new Date(), 3) },
  { key: "in_7d", label: "In 1 week", build: () => nextMorning(new Date(), 7) },
  { key: "in_14d", label: "In 2 weeks", build: () => nextMorning(new Date(), 14) },
  { key: "next_month", label: "Next month", build: () => nextMorning(new Date(), 30) },
];

function toInputValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function SnoozePopover({
  anchorRef,
  onSelect,
  onClose,
  label = "Snooze until",
  align = "left",
}) {
  const popoverRef = useRef(null);
  const [customValue, setCustomValue] = useState(() => toInputValue(nextMorning(new Date(), 3)));

  useEffect(() => {
    const onDocClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorRef?.current?.contains(event.target)) return;
      onClose?.();
    };
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  const commit = (date) => {
    if (!date) return;
    onSelect?.(date);
    onClose?.();
  };

  return (
    <div
      ref={popoverRef}
      className={classNames(
        "absolute z-40 mt-2 w-72 rounded-2xl border border-zinc-200 bg-white p-3 shadow-panel",
        align === "right" ? "right-0" : "left-0"
      )}
    >
      <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        {label}
      </p>
      <div className="space-y-0.5">
        {PRESETS.map((preset) => {
          const date = preset.build();
          return (
            <button
              key={preset.key}
              onClick={() => commit(date)}
              className="flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-sm text-zinc-700 hover:bg-zinc-100"
            >
              <span>{preset.label}</span>
              <span className="text-xs text-zinc-400">{format(date, "EEE, MMM d")}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 border-t border-zinc-100 pt-3">
        <label className="block text-xs font-medium text-zinc-600">Custom date & time</label>
        <div className="mt-1.5 flex items-center gap-2">
          <input
            type="datetime-local"
            value={customValue}
            onChange={(event) => setCustomValue(event.target.value)}
            className="flex-1 rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
          <button
            onClick={() => {
              const date = new Date(customValue);
              if (!Number.isNaN(date.getTime())) commit(date);
            }}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Set
          </button>
        </div>
      </div>
    </div>
  );
}

export { addDays, nextMorning };
